import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  Card,
  CardContent,
  Divider,
  Stack,
  Box,
  CardHeader,
  IconButton,
  Typography,
  Tooltip,
  TextField,
  Button,
} from '@mui/material';
import { useAtomValue } from 'jotai';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WebRoundedIcon from '@mui/icons-material/WebRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import TemplateMenu from './TemplateMenu';
import OutcomesMenu from '../Templates/OutcomesMenu';
import { allOutcomesAtom } from '../../../../Atoms';
import CustomOutlinedChip from '../../../../Components/Chip/CustomOutlinedChip';

const TemplateCard = function TemplateCard({
  name = '',
  templateOutcomes = [],
  onClick = () => {},
  handleEditTemplate = () => {},
  handleDeleteTemplate = () => {},
  handleRenameTemplate = () => {},
  hideOutcomes = false,
  title = '',
  withSubject = false,
}) {
  const intl = useIntl();
  const allOutcomes = useAtomValue(allOutcomesAtom);
  const [templateMenu, setTemplateMenu] = useState(null);
  const [scopesMenu, setScopesMenu] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [inputValue, setInputValue] = useState(name);
  const [subjectValue, setSubjectValue] = useState(title || '');

  const openTemplateMenu = (event) => {
    setTemplateMenu(event.currentTarget);
  };
  const closeTemplateMenu = () => {
    setTemplateMenu(null);
  };

  const openScopesMenu = (event) => {
    setScopesMenu(event.currentTarget);
  };
  const closeScopesMenu = (outcomes) => () => {
    if (
      templateOutcomes.length !== outcomes.length ||
      !templateOutcomes.every(({ outcomeId }) =>
        outcomes.find(({ outcomeId: id }) => id === outcomeId)
      )
    )
      handleEditTemplate({ outcomes });

    setScopesMenu(null);
  };

  const toggleRenaming = () => {
    setRenaming(!renaming);
  };

  const handleChangeInputValue = (event) => {
    setInputValue(event.target.value);
  };

  return (
    <>
      <Box
        sx={{
          pt: '8px',
        }}
      >
        <Card sx={{ width: '100%' }} raised>
          <CardHeader
            disableTypography
            sx={{
              p: '12px',
              '& .MuiCardHeader-action': {
                alignSelf: 'center',
              },
            }}
            avatar={<WebRoundedIcon sx={{ opacity: 0.54 }} />}
            action={
              renaming ? (
                <Button
                  variant="outlined"
                  onClick={() => {
                    toggleRenaming();
                    if (inputValue) {
                      handleRenameTemplate({
                        name: inputValue,
                        title: subjectValue,
                      });
                    }
                  }}
                >
                  <DoneRoundedIcon />
                </Button>
              ) : (
                <IconButton
                  id="template-options-icnBtn"
                  size="small"
                  onClick={openTemplateMenu}
                >
                  <MoreVertIcon />
                </IconButton>
              )
            }
            title={
              renaming ? (
                <Stack spacing={1}>
                  <TextField
                    autoFocus
                    size="small"
                    label={withSubject ? intl.messages.NAME : undefined}
                    value={inputValue}
                    onChange={handleChangeInputValue}
                  />
                  {withSubject && (
                    <TextField
                      id="rename-template-subject-txtfield"
                      size="small"
                      label={intl.messages.SUBJECT}
                      value={subjectValue}
                      onChange={(event) => setSubjectValue(event.target.value)}
                      slotProps={{ htmlInput: { maxLength: 64 } }}
                    />
                  )}
                </Stack>
              ) : (
                <Typography
                  variant="body1"
                  onClick={onClick}
                  sx={{ cursor: 'pointer' }}
                >
                  {name}
                </Typography>
              )
            }
          />
          {!hideOutcomes && (
            <>
              <Divider />
              <CardContent>
                <Stack
                  useFlexGap
                  direction="row"
                  spacing={1}
                  sx={{
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  {templateOutcomes.map(({ outcomeId, outcomeName }) => (
                    <CustomOutlinedChip
                      key={outcomeId}
                      label={
                        intl.messages[
                          outcomeName?.toUpperCase() || 'UNAVAILABLE'
                        ]
                      }
                    />
                  ))}
                  <Tooltip title={intl.messages.ASSIGN_SCOPE}>
                    <IconButton id="add-scope-icnBtn" onClick={openScopesMenu}>
                      <AddCircleOutlineRoundedIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </>
          )}
        </Card>
      </Box>
      <TemplateMenu
        anchor={templateMenu}
        onClose={closeTemplateMenu}
        onDelete={handleDeleteTemplate}
        onRename={toggleRenaming}
      />
      <OutcomesMenu
        anchor={scopesMenu}
        onClose={closeScopesMenu}
        outcomes={allOutcomes}
        defaultSelected={templateOutcomes}
      />
    </>
  );
};

TemplateCard.propTypes = {
  name: PropTypes.string,
  templateOutcomes: PropTypes.array,
  onClick: PropTypes.func,
  handleEditTemplate: PropTypes.func,
  handleDeleteTemplate: PropTypes.func,
  handleRenameTemplate: PropTypes.func,
  hideOutcomes: PropTypes.bool,
};

export default TemplateCard;
